import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram } from "lucide-react";
import { Container } from "../shared/Container";
import { Logo } from "../shared/Logo";
import { getAllSolutions } from "@/lib/content";

const solutions = getAllSolutions();

const FOOTER_LINKS = {
  Solutions: solutions.map((s) => ({
    label: s.title,
    to: `/solutions/${s.id}`,
  })),
  Company: [
    { label: "About", to: "/company/about" },
    { label: "Our Work", to: "/work" },
    { label: "Contact", to: "/company/contact" },
  ],
  Legal: [
    { label: "Privacy Policy", to: "/legal/privacy" },
    { label: "Terms of Service", to: "/legal/terms" },
    { label: "Cookie Policy", to: "/legal/cookies" },
  ],
};

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/creativorare",
    icon: Linkedin,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61588031587878",
    icon: Facebook,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/creativorare/",
    icon: Instagram,
  },
];

export function FooterV2() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative border-t border-brand/25 bg-gradient-to-b from-neutral-900 via-neutral-950 to-black text-neutral-400">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand to-brand-accent"
        aria-hidden="true"
      />
      <Container className="py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-2">
            <Link
              to="/"
              className="inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-v2-sm"
              aria-label="Creativorare — go to homepage"
            >
              <Logo
                className="h-9 w-auto mb-4"
                inverted
                width={120}
                height={36}
              />
            </Link>
            <p className="text-sm leading-relaxed max-w-sm mb-6">
              Enterprise engineering and AI solutions company. We partner with
              ambitious organizations to solve complex business problems through
              modern software, intelligent automation, and digital transformation.
            </p>
            <div className="flex gap-3">
              {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-v2-md bg-neutral-800 flex items-center justify-center hover:bg-gradient-to-br hover:from-brand hover:to-brand-accent transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(FOOTER_LINKS).map(([category, links]) => (
            <nav key={category} aria-label={`${category} navigation`}>
              <h3 className="text-white font-semibold text-sm mb-4">{category}</h3>
              <ul className="space-y-2 list-none m-0 p-0">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-neutral-800 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center text-center sm:text-left">
          <p className="text-xs text-neutral-500">
            © {currentYear} Creativorare. All rights reserved.
          </p>
          <p className="text-xs text-neutral-500 break-words">
            Metro Manila, Philippines
            <span className="hidden sm:inline"> · </span>
            <br className="sm:hidden" />
            <a
              href="mailto:techsolutions@creativorare.com"
              className="hover:text-white transition-colors"
            >
              techsolutions@creativorare.com
            </a>
          </p>
        </div>
      </Container>
    </footer>
  );
}
